import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export const useScrollReveal = (config = {}) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const {
      from = { opacity: 0, y: 60 },
      to = { opacity: 1, y: 0, duration: 1, ease: "power3.out" },
      trigger = el,
      start = "top 85%",
      toggleActions = "play none none reverse",
      stagger = 0,
    } = config;

    const ctx = gsap.context(() => {
      const targets = stagger ? el.children : el;
      gsap.set(targets, from);
      ScrollTrigger.create({
        trigger,
        start,
        toggleActions,
        onEnter: () => {
          gsap.to(targets, {
            ...to,
            stagger: stagger || undefined,
          });
        },
      });
    });

    return () => ctx.revert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return ref;
};

export const useParallax = (config = {}) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const { speed = 0.15, start = "top bottom", end = "bottom top" } = config;

    const ctx = gsap.context(() => {
      gsap.to(el, {
        y: () => el.offsetHeight * speed,
        ease: "none",
        scrollTrigger: {
          trigger: el.parentElement || el,
          start,
          end,
          scrub: true,
        },
      });
    });

    return () => ctx.revert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return ref;
};

export const usePinSection = (config = {}) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const { start = "top top", end = "+=100%", pinSpacing = true, disableOnMobile = true } = config;

    const ctx = gsap.context(() => {
      let scrollTriggerInstance;

      const createTrigger = () => {
        if (disableOnMobile && window.innerWidth < 1024) return;
        scrollTriggerInstance = ScrollTrigger.create({
          trigger: el,
          start,
          end,
          pin: true,
          pinSpacing,
          scrub: 1,
        });
      };

      createTrigger();

      const mm = gsap.matchMedia();
      if (disableOnMobile) {
        mm.add("(min-width: 1024px)", () => {
           if (!scrollTriggerInstance) createTrigger();
           return () => {
             if (scrollTriggerInstance) {
                scrollTriggerInstance.kill();
                scrollTriggerInstance = null;
             }
           }
        });
      }
    });

    return () => ctx.revert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return ref;
};
